<template>
  <div class="crop-variety-settings">
    <v-card class="settings-card" elevation="0">

      <!-- Header -->
      <div class="d-flex justify-end mb-6">
        <v-btn
          class="text-none"
          height="36"
          color="primary"
          outlined
          @click="handleBack"
        >
          <span class="text-caption font-weight-medium">BACK</span>
        </v-btn>
      </div>

      <!-- Page Title -->
      <div class="settings-content">
        <div class="d-flex align-center justify-space-between mb-2">
          <h2 class="section-title">{{ $t('setting.cropVarietySettings') }}</h2>
        </div>
        <p class="text-body-1 mb-6">
          {{ $t('setting.cropVarietyDesc') }}
        </p>

        <!-- Table Card -->
        <v-card outlined>

          <!-- Table Toolbar -->
          <v-card-title class="table-toolbar pa-4">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search by variety name..."
              outlined
              dense
              hide-details
              clearable
              style="max-width: 320px;"
              @input="onSearchInput"
            />
            <v-spacer />
            <v-btn
              color="primary"
              height="40"
              class="text-none"
              @click="openAddModal"
            >
              <v-icon left small>mdi-plus</v-icon>
              Add New
            </v-btn>
          </v-card-title>

          <!-- Data Table -->
          <v-data-table
            :headers="headers"
            :items="varieties"
            :loading="loading"
            :server-items-length="totalRows"
            :options.sync="tableOptions"
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
            loading-text="Loading crop varieties..."
            no-data-text="No crop varieties found."
            class="crop-variety-table"
          >
            <!-- Serial Number column -->
            <template #item.sn="{ index }">
              {{ (tableOptions.page - 1) * tableOptions.itemsPerPage + index + 1 }}
            </template>

            <!-- Crop Type column -->
            <template #item.cropType="{ item }">
              <v-chip
                v-if="item.Option"
                small
                color="primary"
                text-color="white"
                class="font-weight-medium"
              >
                {{ item.Option.name }}
              </v-chip>
              <span v-else class="text--secondary">—</span>
            </template>

            <!-- Created Date column -->
            <template #item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>

            <!-- Action column -->
            <template #item.actions="{ item }">
              <v-menu offset-y left>
                <template #activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openEditModal(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="primary">mdi-pencil-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openDeleteConfirm(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="error">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-card>

    <!-- ── Add / Edit Modal ───────────────────────────────────────────────── -->
    <v-dialog v-model="modal.open" max-width="480" persistent>
      <v-card>
        <v-card-title class="modal-title pa-5">
          <span class="text-h6 font-weight-semibold">
            {{ modal.isEdit ? $t('setting.editCropVariety') : $t('setting.addCropVariety') }}
          </span>
          <v-spacer />
          <v-btn icon small @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5">
          <v-form ref="varietyForm" v-model="modal.valid" lazy-validation>

            <label class="form-label">{{ $t('setting.cropVarietyName') }} <span class="error--text">*</span></label>
            <v-text-field
              v-model="modal.form.name"
              :placeholder="$t('setting.cropVarietyName')"
              outlined
              dense
              :rules="rules.name"
              class="mb-4"
              hide-details="auto"
            />

            <label class="form-label">{{ $t('setting.cropType') }} <span class="error--text">*</span></label>
            <v-select
              v-model="modal.form.cropTypeOptId"
              :items="cropTypeOptions"
              item-text="name"
              item-value="id"
              :placeholder="$t('setting.cropType')"
              outlined
              dense
              :rules="rules.cropTypeOptId"
              :loading="loadingCropTypes"
              hide-details="auto"
              no-data-text="No crop types available"
            />

          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            outlined
            height="40"
            width="100"
            color="primary"
            class="text-none mr-2"
            @click="closeModal"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            height="40"
            width="100"
            class="text-none"
            :loading="modal.saving"
            @click="submitModal"
          >
            {{ modal.isEdit ? 'Update' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Delete Confirmation Dialog ────────────────────────────────────── -->
    <v-dialog v-model="deleteDialog.open" max-width="420" persistent>
      <v-card>
        <v-card-title class="modal-title pa-5">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span class="text-h6 font-weight-semibold">{{ $t('setting.deleteCropVariety') }}</span>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5">
          <p class="text-body-1 mb-0">
            {{ $t('setting.deleteCropVarietyConfirm') }}
            <br />
            <strong>{{ deleteDialog.item && deleteDialog.item.name }}</strong>
          </p>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            outlined
            height="40"
            width="100"
            color="primary"
            class="text-none mr-2"
            @click="deleteDialog.open = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            height="40"
            width="100"
            class="text-none"
            :loading="deleteDialog.loading"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import CropAndVariety from '@/_services/CropAndVariety';

const EMPTY_FORM = () => ({ name: '', cropTypeOptId: null });

export default {
  name: 'CropTypeAndVarietySetting',

  data() {
    return {
      // Table
      loading: false,
      varieties: [],
      totalRows: 0,
      search: '',
      searchTimer: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },

      // Crop type dropdown
      cropTypeOptions: [],
      loadingCropTypes: false,

      // Add / Edit modal
      modal: {
        open: false,
        isEdit: false,
        valid: true,
        saving: false,
        editId: null,
        form: EMPTY_FORM(),
      },

      // Delete dialog
      deleteDialog: {
        open: false,
        loading: false,
        item: null,
      },

      rules: {
        name: [
          (v) => !!v || 'Variety name is required',
          (v) => (v && v.trim().length >= 2) || 'Name must be at least 2 characters',
        ],
        cropTypeOptId: [
          (v) => !!v || 'Crop type is required',
        ],
      },
    };
  },

  watch: {
    tableOptions: {
      deep: true,
      handler() {
        this.fetchVarieties();
      },
    },
  },

  async mounted() {
    await this.fetchCropTypeOptions();
  },

  computed: {
    headers() {
      return [
        { text: 'S.N',                             value: 'sn',        sortable: false, width: '60px'  },
        { text: this.$t('setting.cropType'),        value: 'cropType.name',  sortable: false },
        { text: this.$t('setting.cropVarietyName'), value: 'name',      sortable: true  },
        { text: 'Created Date',                     value: 'createdAt', sortable: true  },
        { text: 'Action',                           value: 'actions',   sortable: false, align: 'center', width: '80px' },
      ];
    },
  },

  methods: {
    // ── Data Fetching ───────────────────────────────────────────────────────

    async fetchVarieties() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.tableOptions;
        const result = await CropAndVariety.getCropVarieties({
          page,
          limit: itemsPerPage,
          search: this.search || '',
        });
        if (result.success) {
          this.varieties = result.data.rows;
          this.totalRows = result.data.totalRows;
        }
      } catch (err) {
        this.$notify({ title: 'Failed to load crop varieties', type: 'error' });
      } finally {
        this.loading = false;
      }
    },

    async fetchCropTypeOptions() {
      this.loadingCropTypes = true;
      try {
        const result = await CropAndVariety.getCropTypeOptions();
        if (result.success) {
          this.cropTypeOptions = result.data.cropTypes;
        }
      } catch (err) {
        this.$notify({ title: 'Failed to load crop types', type: 'error' });
      } finally {
        this.loadingCropTypes = false;
      }
    },

    // ── Search (debounced) ──────────────────────────────────────────────────

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.tableOptions.page = 1;
        this.fetchVarieties();
      }, 400);
    },

    // ── Modal ───────────────────────────────────────────────────────────────

    openAddModal() {
      this.modal.isEdit  = false;
      this.modal.editId  = null;
      this.modal.form    = EMPTY_FORM();
      this.modal.open    = true;
      this.$nextTick(() => this.$refs.varietyForm?.resetValidation());
    },

    openEditModal(item) {
      this.modal.isEdit  = true;
      this.modal.editId  = item.id;
      this.modal.form    = { name: item.name, cropTypeOptId: item.cropTypeOptId };
      this.modal.open    = true;
      this.$nextTick(() => this.$refs.varietyForm?.resetValidation());
    },

    closeModal() {
      this.modal.open = false;
    },

    async submitModal() {
      if (!this.$refs.varietyForm.validate()) return;

      this.modal.saving = true;
      try {
        if (this.modal.isEdit) {
          await CropAndVariety.updateCropVariety(this.modal.editId, this.modal.form);
          this.$notify({ title: 'Crop variety updated successfully', type: 'success' });
        } else {
          await CropAndVariety.createCropVariety(this.modal.form);
          this.$notify({ title: 'Crop variety added successfully', type: 'success' });
        }
        this.closeModal();
        this.fetchVarieties();
      } catch (err) {
        const msg = err?.data?.message || 'Something went wrong. Please try again.';
        this.$notify({ title: msg, type: 'error' });
      } finally {
        this.modal.saving = false;
      }
    },

    // ── Delete ──────────────────────────────────────────────────────────────

    openDeleteConfirm(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.open = true;
    },

    async confirmDelete() {
      this.deleteDialog.loading = true;
      try {
        await CropAndVariety.deleteCropVariety(this.deleteDialog.item.id);
        this.$notify({ title: 'Crop variety deleted successfully', type: 'success' });
        this.deleteDialog.open = false;
        this.fetchVarieties();
      } catch (err) {
        this.$notify({ title: 'Failed to delete crop variety', type: 'error' });
      } finally {
        this.deleteDialog.loading = false;
      }
    },

    // ── Helpers ─────────────────────────────────────────────────────────────

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
      });
    },

    handleBack() {
      this.$router.push({ name: 'enterpriseGeneralSettings' });
    },
  },
};
</script>

<style scoped lang="scss">
.crop-variety-settings {
  .settings-card {
    padding: 24px;
  }

  .settings-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-title {
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }

  .table-toolbar {
    border-bottom: 1px solid #e0e0e0;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
  }

  .modal-title {
    background-color: #fafafa;
  }

  ::v-deep .crop-variety-table {
    .v-data-table__wrapper {
      overflow-x: auto;
    }

    .v-data-table-header th {
      font-size: 13px;
      font-weight: 600;
      color: #333333 !important;
      white-space: nowrap;
    }

    .v-data-table-header th .v-data-table-header__icon {
      color: #333333 !important;
    }

    tbody tr:hover {
      background-color: #f5f9ff !important;
    }
  }

  ::v-deep .v-messages__message {
    line-height: 14px;
  }
}
</style>
